import React, { useContext, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useIsoLayoutEffect } from "shared/lib/react/useIsoLayoutEffect"

import isEqual from "lodash-es/isEqual"

import { connect, currentPage, navigateTo } from "scrivito"
import { LANDING } from "shared/constants/permalinks"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import usePrevious from "shared/lib/usePrevious"

export interface NavigationContextType {
  currentObjID: string | null
  previousObjID: string | null
  currentPath: string
  previousPath: string | null
  back: () => void
  subscribe: (onNavigate: () => void) => () => void
  notify: () => void
}

export const NavigationContext =
  React.createContext<NavigationContextType | null>(null)

function NavigationContextProviderComponent({
  children
}: {
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const currentPageObj = currentPage() || null
  const currentObjID = currentPageObj?.id() || null

  const previousPageObj =
    usePrevious(currentPageObj, (prev, next) => prev?.id() !== next?.id()) ||
    null
  const previousObjID = previousPageObj?.id() || null

  const currentPath = useLocation().pathname
  const previousPath = usePrevious(currentPath) || null

  const currentLocation = useMemo(() => {
    if (currentPageObj) {
      return { type: "page", id: currentObjID }
    }
    return { type: "path", path: currentPath }
  }, [currentObjID, currentPath])

  const previousLocation = usePrevious(
    currentLocation,
    (prev, next) => !isEqual(prev, next)
  )

  const [subscriptions, setSubscriptions] = React.useState<
    { id: string; onNavigate: () => void }[]
  >([])

  const handleBack = useEvent(() => {
    if (previousLocation?.type === "page" && previousPageObj) {
      navigateTo(previousPageObj)
    } else if (!previousPath || previousLocation?.path === previousPath) {
      navigate(LANDING)
    } else {
      navigate(previousPath)
    }
  })

  const handleSubscribe = useEvent((onNavigate: () => void) => {
    const id = generateUUID()
    setSubscriptions(subs => [...subs, { id, onNavigate }])
    return () => {
      setSubscriptions(subs => subs.filter(sub => sub.id !== id))
    }
  })

  useIsoLayoutEffect(() => {
    if ((currentObjID || previousObjID) && currentObjID !== previousObjID) {
      subscriptions.forEach(sub => sub.onNavigate())
    }
  }, [currentObjID, previousObjID])

  useIsoLayoutEffect(() => {
    if (!currentObjID && !previousObjID && currentPath !== previousPath) {
      subscriptions.forEach(sub => sub.onNavigate())
    }
  }, [currentPath, previousPath])

  const handleNotify = useEvent(() => {
    subscriptions.forEach(sub => sub.onNavigate())
  })

  const value = useMemo<NavigationContextType>(
    () => ({
      currentObjID,
      previousObjID,
      currentPath,
      previousPath,
      back: handleBack,
      subscribe: handleSubscribe,
      notify: handleNotify
    }),
    [
      currentObjID,
      previousObjID,
      currentPath,
      previousPath,
      handleBack,
      handleSubscribe,
      handleNotify
    ]
  )

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  )
}

export const NavigationContextProvider = connect(
  NavigationContextProviderComponent
)

export function useNavigationContext() {
  const context = useContext(NavigationContext)

  if (!context) {
    throw new Error(
      "useNavigationContext must be used within a NavigationContextProvider"
    )
  }

  return context
}
